import { PASSWORD_RULES, REGISTER_MESSAGES, useValidatedForm, validateNewPassword } from '@words-happen/screens';
import {
  AUTH_PROVIDERS,
  type LexiMood,
  SignInPrompt,
  type SocialProvider,
  WordsHappenButton,
  WordsHappenFormAlert,
  WordsHappenPasswordField,
} from '@words-happen/ui-components';
import type React from 'react';
import { useEffect, useRef } from 'react';
import type { PasswordResetLinkFailure } from '../../api';
import { MessageScreen, messageScreenActionsClassName, messageScreenFormClassName } from '../../components';

/**
 * What ResetPasswordScreen shows: the form, the link being checked, why it can't be used, that it couldn't be
 * checked, or that the account signs in with a social provider and has no password to reset.
 */
export type ResetPasswordState = 'checking' | 'form' | PasswordResetLinkFailure | 'failed' | 'social-only';

export interface ResetPasswordScreenProps {
  state: ResetPasswordState;
  /** The account's address, from the link; shown on the form and kept for password managers. */
  email?: string;
  /** The new password is being set, or the player signed in: the submit button shows its spinner. */
  submitting?: boolean;
  /** A form-level error, or why the link couldn't be checked (`failed`). Shown in a WordsHappenFormAlert. */
  error?: string;
  /** A password the server rejected, shown on the field until the caller clears it; it gets focus when it appears. */
  passwordError?: string;
  /** Called with the new password, as typed, once it is within the policy. */
  onSubmit: (password: string) => void;
  /** "Try again": checks the link again, for `failed`. */
  onRetry: () => void;
  /** "Send a new link", for an expired or invalid link. */
  onRequestNewLink: () => void;
  /** "Back to sign in". */
  onSignIn: () => void;
  /** For `social-only`: the providers the account signs in with, named in the message when known. */
  socialProviders?: readonly SocialProvider[];
}

interface Copy {
  title: string;
  mood: LexiMood;
  body: string;
}

/** "Google", "Apple or Google", or undefined when no provider is known. */
export const providerNames = (providers: readonly SocialProvider[]): string | undefined =>
  providers.length > 0 ? providers.map((provider) => AUTH_PROVIDERS[provider].name).join(' or ') : undefined;

/** The message for an account that only signs in with a social provider, naming it when known. */
export const socialOnlyCopy = (providers: readonly SocialProvider[]): Copy => {
  const names = providerNames(providers);
  return names
    ? {
        title: `This account signs in with ${names}`,
        mood: 'happy',
        body: `It doesn't have a password. Use the ${names} button to sign in.`,
      }
    : {
        title: 'This account signs in another way',
        mood: 'happy',
        body: "It doesn't have a password. Sign in the way you signed up.",
      };
};

const COPY: Readonly<Record<Exclude<ResetPasswordState, 'form' | 'social-only'>, Copy>> = {
  checking: { title: 'Checking your link…', mood: 'focused', body: 'One moment.' },
  expired: {
    title: 'This link has expired',
    mood: 'surprised',
    body: 'Reset links only work for a limited time. We can send you a new one.',
  },
  invalid: {
    title: "This link doesn't work",
    mood: 'surprised',
    body: 'It may be incomplete, out of date or already used. We can send you a new one.',
  },
  failed: { title: "We couldn't check your link", mood: 'surprised', body: 'Your link may still be good.' },
};

const VALIDATORS = { password: validateNewPassword };

/** The new password form, for a link that checked out. A hidden username field tells password managers whose it is. */
const NewPasswordForm: React.FC<
  Omit<ResetPasswordScreenProps, 'state' | 'onRetry' | 'onRequestNewLink' | 'onSignIn'>
> = ({ email, submitting = false, error, passwordError, onSubmit }) => {
  const { values, errors, change, blur, validateAll } = useValidatedForm({ password: '' }, VALIDATORS);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (passwordError) {
      passwordRef.current?.focus();
    }
  }, [passwordError]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    if (validateAll()) {
      passwordRef.current?.focus();
      return;
    }
    onSubmit(values.password);
  };

  return (
    <form className={messageScreenFormClassName} aria-label="Choose a new password" noValidate onSubmit={handleSubmit}>
      <input type="email" name="username" autoComplete="username" value={email ?? ''} readOnly hidden />
      <WordsHappenPasswordField
        ref={passwordRef}
        label="New password"
        name="password"
        autoComplete="new-password"
        passwordrules={PASSWORD_RULES}
        hint={REGISTER_MESSAGES.passwordHint}
        value={values.password}
        error={errors.password ?? passwordError}
        onChange={change('password')}
        onBlur={blur('password')}
      />

      {error ? <WordsHappenFormAlert>{error}</WordsHappenFormAlert> : null}

      <WordsHappenButton type="submit" variant="coral" size="lg" loading={submitting}>
        Set password
      </WordsHappenButton>
    </form>
  );
};

/**
 * /reset-password: the new password form for a reset link, or why the link can't be used. An expired or invalid
 * link offers a new one; a link that couldn't be checked offers to try again. An account that only signs in with a
 * social provider gets no form, only the way back to sign in.
 */
export const ResetPasswordScreen: React.FC<ResetPasswordScreenProps> = ({
  state,
  onRetry,
  onRequestNewLink,
  onSignIn,
  socialProviders = [],
  ...form
}) => {
  if (state === 'form') {
    return (
      <MessageScreen title="Choose a new password" mood="focused" action={<NewPasswordForm {...form} />}>
        <p>
          For <strong>{form.email}</strong>.
        </p>
      </MessageScreen>
    );
  }

  const { title, mood, body } = state === 'social-only' ? socialOnlyCopy(socialProviders) : COPY[state];

  const action = () => {
    switch (state) {
      case 'checking':
        return null;
      case 'social-only':
        return (
          <WordsHappenButton variant="coral" size="lg" onClick={onSignIn}>
            Back to sign in
          </WordsHappenButton>
        );
      case 'failed':
        return (
          <>
            {form.error ? <WordsHappenFormAlert>{form.error}</WordsHappenFormAlert> : null}
            <WordsHappenButton variant="coral" size="lg" onClick={onRetry}>
              Try again
            </WordsHappenButton>
            <SignInPrompt prefix={null} action="Back to sign in" onAction={onSignIn} />
          </>
        );
      default:
        return (
          <>
            <WordsHappenButton variant="coral" size="lg" onClick={onRequestNewLink}>
              Send a new link
            </WordsHappenButton>
            <SignInPrompt prefix={null} action="Back to sign in" onAction={onSignIn} />
          </>
        );
    }
  };

  return (
    <MessageScreen title={title} mood={mood} action={<div className={messageScreenActionsClassName}>{action()}</div>}>
      <p role={state === 'checking' ? 'status' : undefined}>{body}</p>
    </MessageScreen>
  );
};
